import { CurrentUserAttributes, useUserAttributes } from '@peerless/ui-amplify';
import { useEffect, useRef, useState } from 'react';

export type ModularSeriesUserInfo =
  | { loggedIn: false }
  | {
      loggedIn: true;
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      accepts_marketing: boolean;
    };

export function mapUserInfo(user: CurrentUserAttributes | undefined): ModularSeriesUserInfo {
  if (!user) {
    return { loggedIn: false };
  }

  return {
    loggedIn: true,
    id: user.sub ?? '',
    first_name: user.given_name ?? '',
    last_name: user.family_name ?? '',
    email: user.email ?? '',
    accepts_marketing: user.email_verified === 'true',
  };
}

export type OnAuthenticated = (user: CurrentUserAttributes) => void;

export interface AuthenticationInteropService {
  user: CurrentUserAttributes | undefined;
  authenticate(onAuthenticated: OnAuthenticated): void;
}

export function useAuthenticationInteropService(
  user?: CurrentUserAttributes
): [AuthenticationInteropService, OnAuthenticated?] {
  // The presence of a callback is used control visibility of the Authenticator component.
  const [callback, setCallback] = useState<OnAuthenticated>();

  // The service is a singleton, so we use a ref to keep the same instance across renders.
  const service = useRef<AuthenticationInteropService>({
    user,
    authenticate(onAuthenticated: OnAuthenticated) {
      setCallback(
        () =>
          function onAuthenticatedHandler(user: CurrentUserAttributes) {
            setCallback(undefined);
            onAuthenticated(user);
          }
      );
    },
  });

  return [service.current, callback];
}

export interface AuthenticationInteropControllerProps {
  callback: OnAuthenticated;
}

export function AuthenticationInteropController({
  callback,
}: AuthenticationInteropControllerProps) {
  const user = useUserAttributes();

  useEffect(() => {
    if (user) {
      // If we have a user, we need to call the callback with the user info.
      // This will ultimately provide the user info to the configurator iframe.
      callback(user);
    }
  }, [user, callback]);

  return null;
}
